import type { Reference, StrongReference } from '@platform/entities/entity.class';
import config from '@platform/shared/config';
import { normalize } from '@platform/shared/helpers/formatters';
import { computed, reactive } from 'vue';
import type { sys_option } from '@platform/layouts/sys_option/sys_option';
import type { UserRoleParams } from '@platform/shared/lib/roles';

/**
 * @deprecated Скоро откажемся от старых ролей :)
 */
export const sysRoles = {
    ADMIN: 'ROLE_ADMIN',
    MANAGER: 'ROLE_MANAGER',
    SUPPLY: 'ROLE_SUPPLIER',
    PTO: 'ROLE_PTO',
    DOC: 'ROLE_DOC',
    LAWYER: 'ROLE_LAWYER',
    PTO_CHIEF: 'ROLE_PTO_CHIEF',
    ACCOUNTANT: 'ROLE_ACCOUNTANT',
    GUEST: 'ROLE_GUEST',
    CURATOR: 'ROLE_CURATOR',
};

export type RpcSysParams = {
    sysServiceId: string;
    sysOrgId?: string | null;
    sysWithoutOrgHierarchy: boolean;
    currUserUuid?: string | null;
    currExtUserId?: number | null;
};

export type IUserState = {
    user:
        | null
        | undefined
        | {
              active: boolean;
              is_notification_muted: boolean;
              description: string;
              email: string | null;
              phone: string | null;
              extUserId: number;
              name: string;
              /**
               * @deprecated Поле старых ролей, которое скоро перестанет работать
               */
              roles: string[];
              role: UserRoleParams;
              fio: {
                  name: string;
                  surname: string;
                  secname: string;
              };
              person: any;
              person_id: string | null; // todo: check
              sys_user_id: string;
              id: string;
              username: string;
              isGuest: boolean;
              employee_id?: string;
              employee_post?: string;
              /**
               * ID области контрагента
               */
              sys_contragent_scope_id?: string;
              /**
               * Мультиорганизационный учет
               */
              multiOrganizations?: {
                  /**
                   * Учет включен для текущего пользователя
                   */
                  enabled: boolean;
                  /**
                   * Фильрация по организациям включена на сервере
                   */
                  filtered: boolean;
                  /**
                   * В области имеется несколько организаций
                   */
                  used: boolean;
                  /**
                   * Список организаций, в которых работает сотрудник (platform.contragent)
                   */
                  items: StrongReference[];
              };
              currentEmployeeId: Reference; //возвращает employee_id по текущему авторизованному пользователю
              currentEmployeeProjectsId: string[]; // возвращает список project_id доступности по проектам
              carrotQuestHash: string; // хеш для авторизации в carrot quest
              sys_system_template_code_name: string;
              sys_system_template_id: number;
          };
    org: null | {
        main: {
            sys_service_org_link_id?: any;
            label: string;
            name: string;
            value: string;
            post: string | null;
            contragent_id: string;
            used_sys_system_template_id: string;
        };
        all: {
            id: string;
            contragent_id: string;
            level: number;
            main: boolean;
            name: string;
            post: string;
            roles: string[];
            sys_service_id: string;
            sys_service_org_link_id: Reference;
            used_sys_system_template_id: string;
        }[];
    };
    sysParams: RpcSysParams;
    sysDocStateSetting: any;
    sysOptions: sys_option | null;
    autoLogin?: null | { login: string; password: string };
};
const saveSysParams = (sysParams: RpcSysParams | null) => {
    localStorage.setItem('SYS_PARAMS', JSON.stringify(sysParams));
};
const loadSysParams = () => {
    const sysParams = localStorage.getItem('SYS_PARAMS');
    return sysParams
        ? JSON.parse(sysParams)
        : {
              sysServiceId: config.serviceId,
              sysWithoutOrgHierarchy: false,
          };
};
const userState = reactive<IUserState>({
    user: undefined,
    org: null,
    sysDocStateSetting: null,
    sysOptions: null,
    sysParams: loadSysParams(),
    autoLogin: null,
});

const isLoggedIn = computed(() => userState.user != null);
const setUserState = (user: any, org: any) => {
    userState.org = org;
    userState.user = {
        ...user,
        person_id: normalize(user.person_id),
    };
};

const setSysParams = (sysParams: Partial<RpcSysParams>) => {
    userState.sysParams = {
        sysServiceId: config.serviceId,
        sysWithoutOrgHierarchy: false,
        ...sysParams,
    };
    saveSysParams(userState.sysParams);
};

const setSysDocStateSetting = (sysDocStateSetting: any) => {
    userState.sysDocStateSetting = sysDocStateSetting;
};

const setSysOptions = (sysOptions: sys_option) => {
    userState.sysOptions = sysOptions;
};

const orgList = computed(
    () =>
        userState.org?.all.map((org) => ({
            ...org,
            name: org.name,
            label: org.name,
            value: org.id,
        })) || []
);

const currentOrg = computed({
    get: () => orgList.value.find((org) => org.value === userState.sysParams?.sysOrgId),
    set: (val) => {
        userState.sysParams = {
            ...userState.sysParams,
            sysOrgId: val?.value,
        };
    },
});

const setAsNotLoggedIn = () => {
    userState.user = null;
    userState.org = null;
    userState.sysDocStateSetting = null;
    setSysParams({
        sysServiceId: config.serviceId,
        sysWithoutOrgHierarchy: false,
    });
};

const setAutoLogin = (options: null | { login: string; password: string }) => {
    userState.autoLogin = options;
};

export default {
    userState,
    isLoggedIn,
    setUserState,
    setAsNotLoggedIn,
    setSysParams,
    setSysDocStateSetting,
    setSysOptions,
    orgList,
    currentOrg,
    setAutoLogin,
    useLoginState() {
        const isLoggedIn = computed(() => userState.user !== null);
        return { isLoggedIn, user: computed(() => userState.user), org: computed(() => userState.org) };
    },
};
